import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Wallet, Plus, Trash2 } from "lucide-react";
import { canAccessModule } from "@/utils/permissions";
import "./cabina.css";

const fmt = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

export default function AdminPagosGlobales() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAplicar, setShowAplicar] = useState(false);
  const isAdmin = canAccessModule(user, "admin_pagos_globales");

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [pmts, inv, cc] = await Promise.all([
        axios.get(`${API}/facturacion-global/payments`),
        axios.get(`${API}/facturacion-global/invoices`),
        cat ? Promise.resolve({ data: cat }) : axios.get(`${API}/cabina/catalogo`),
      ]);
      setPayments(pmts.data.payments || []);
      setInvoices(inv.data.invoices || []);
      if (!cat) setCat(cc.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar");
    }
    setLoading(false);
  }, [isAdmin, cat]);

  useEffect(() => { load(); }, [load]);

  const eliminar = async (p) => {
    const motivo = window.prompt(`¿Motivo para borrar el pago (mínimo 3 caracteres)?`, "");
    if (!motivo || motivo.trim().length < 3) return;
    if (!window.confirm(`Se borrará el pago y sus sombras en cada expediente. ¿Continuar?`)) return;
    try {
      await axios.delete(`${API}/facturacion-global/payments/${p.id}`, { params: { motivo } });
      toast.success("Pago borrado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!isAdmin) {
    return (
      <div className="cv" style={{ padding: 22 }}>
        <div className="rulebox" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
          <b>Sin acceso.</b> No tienes permiso para aplicar pagos globales.
        </div>
      </div>
    );
  }

  const invoicesConSaldo = invoices.filter((i) => i.metodo === "PPD" && (i.saldo || 0) > 0.5);

  return (
    <div className="cv" style={{ padding: 22 }} data-testid="admin-pagos-globales">
      <div className="head">
        <div>
          <h2>Pagos Globales</h2>
          <p>Aplica un pago a una factura global PPD. El importe se distribuye entre los expedientes involucrados (prorrateo sugerido, editable).</p>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn dark sm" onClick={() => setShowAplicar(true)} disabled={invoicesConSaldo.length === 0} data-testid="btn-aplicar-pg">
            <Plus size={13} /> Aplicar pago
          </button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="tw">
          {loading ? <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Cargando…</p>
            : payments.length === 0 ? <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Sin pagos aplicados.</p>
            : (
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Factura global</th><th>Forma</th><th>Medio</th>
                    <th>Referencia</th><th style={{ textAlign: "center" }}>Expedientes</th>
                    <th className="r">Importe</th>
                    <th className="r" style={{ width: 90 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const inv = invoices.find((i) => i.id === p.global_invoice_id);
                    return (
                      <tr key={p.id} data-testid={`row-pg-${p.id}`}>
                        <td className="mono">{fmtDate(p.fecha)}</td>
                        <td><b>{inv?.folio || p.global_invoice_id.slice(0, 8)}</b> <span style={{ color: "var(--cv-muted)", fontSize: 11 }}>{inv?.client_name || ""}</span></td>
                        <td>{p.forma}</td>
                        <td>{p.medio_pago || "—"}</td>
                        <td>{p.referencia || "—"}</td>
                        <td style={{ textAlign: "center" }}>{(p.distribucion || []).length}</td>
                        <td className="r mono"><b>{fmt(p.importe_total)}</b></td>
                        <td className="r">
                          <button className="btn ghost sm" onClick={() => eliminar(p)} title="Borrar" style={{ color: "#991b1b", borderColor: "#fca5a5" }} data-testid={`btn-borrar-pg-${p.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
      </div>

      {showAplicar && (
        <AplicarPagoDialog cat={cat} invoices={invoicesConSaldo} onClose={() => setShowAplicar(false)} onSaved={() => { setShowAplicar(false); load(); }} />
      )}
    </div>
  );
}

// -------- Aplicar Pago Global --------
function AplicarPagoDialog({ cat, invoices, onClose, onSaved }) {
  const [invoiceId, setInvoiceId] = useState("");
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    importe: 0, forma: "", medio_pago: "", referencia: "",
  });
  const [dist, setDist] = useState({}); // {exp_id: {shadow_id, importe_aplicado, saldo}}
  const [saldosShadow, setSaldosShadow] = useState({}); // {exp_id: saldo}

  const inv = invoices.find((i) => i.id === invoiceId);

  // Cargar saldos por expediente al elegir la factura
  useEffect(() => {
    if (!inv) { setDist({}); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/facturacion-global/invoices/${inv.id}`);
        const full = r.data;
        const d = {};
        const saldos = {};
        // Calcular saldo por sombra a partir de los pagos globales aplicados
        const pagosPorExp = {};
        (full.pagos_globales || []).forEach((p) => {
          (p.distribucion || []).forEach((x) => {
            pagosPorExp[x.expediente_id] = (pagosPorExp[x.expediente_id] || 0) + Number(x.importe_aplicado || 0);
          });
        });
        (full.expedientes || []).forEach((row) => {
          const saldo = Number(row.total_parcial || 0) - (pagosPorExp[row.expediente_id] || 0);
          d[row.expediente_id] = { shadow_id: row.shadow_factura_id, importe_aplicado: 0, saldo, total_parcial: row.total_parcial, folio: row.folio_expediente };
          saldos[row.expediente_id] = saldo;
        });
        setDist(d);
        setSaldosShadow(saldos);
        setForm((f) => ({ ...f, importe: Number(inv.saldo || 0) }));
      } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    })();
  }, [invoiceId, inv]);

  // Prorratear automáticamente
  const prorratear = () => {
    const total = Number(form.importe || 0);
    const saldoTotal = Object.values(saldosShadow).reduce((a, b) => a + b, 0);
    if (saldoTotal <= 0) { toast.error("No hay saldo pendiente"); return; }
    const newDist = { ...dist };
    let remaining = total;
    const keys = Object.keys(dist);
    keys.forEach((k, idx) => {
      const saldo = saldosShadow[k];
      let porc = saldo / saldoTotal;
      let asignado = idx === keys.length - 1 ? remaining : Math.round(total * porc * 100) / 100;
      if (asignado > saldo) asignado = saldo;
      newDist[k] = { ...newDist[k], importe_aplicado: asignado };
      remaining = Math.round((remaining - asignado) * 100) / 100;
    });
    setDist(newDist);
  };

  const updateImporte = (expId, val) => {
    setDist((d) => ({ ...d, [expId]: { ...d[expId], importe_aplicado: Number(val || 0) } }));
  };

  const sumDist = useMemo(() => {
    return Object.values(dist).reduce((a, b) => a + Number(b.importe_aplicado || 0), 0);
  }, [dist]);

  const submit = async () => {
    if (!inv) { toast.error("Elige una factura global"); return; }
    if (Number(form.importe) <= 0) { toast.error("Importe inválido"); return; }
    if (!form.forma) { toast.error("Elige forma de pago SAT"); return; }
    const distribucion = Object.entries(dist)
      .filter(([, v]) => Number(v.importe_aplicado || 0) > 0)
      .map(([expediente_id, v]) => ({ expediente_id, importe_aplicado: Number(v.importe_aplicado) }));
    if (distribucion.length === 0) { toast.error("Distribuye el importe"); return; }
    if (Math.abs(sumDist - Number(form.importe)) > 0.5) {
      toast.error(`La distribución (${fmt(sumDist)}) no coincide con el importe (${fmt(form.importe)})`);
      return;
    }
    try {
      const body = {
        global_invoice_id: inv.id,
        fecha: form.fecha,
        importe_total: Number(form.importe),
        forma: form.forma,
        medio_pago: form.medio_pago,
        referencia: form.referencia,
        distribucion,
      };
      await axios.post(`${API}/facturacion-global/payments`, body);
      toast.success("Pago aplicado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="cv" style={{ maxWidth: 900, padding: 0, maxHeight: "94vh", overflow: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--cv-line)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}><Wallet size={14} style={{ verticalAlign: -2, marginRight: 6 }} /> Aplicar pago global</h3>
          <p style={{ margin: "4px 0 0", color: "var(--cv-muted)", fontSize: 12.5 }}>Elige la factura global PPD, indica el importe y distribuye entre los expedientes involucrados.</p>
        </div>
        <div style={{ padding: 20 }}>
          <div className="grid" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
            <label className="f">
              <span>Factura global (PPD, con saldo)</span>
              <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} data-testid="pg-invoice">
                <option value="">Elige…</option>
                {invoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.folio || i.id.slice(0, 8)} · {i.client_name} · saldo {fmt(i.saldo)}
                  </option>
                ))}
              </select>
            </label>
            <label className="f"><span>Fecha</span><input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></label>
            <label className="f"><span>Importe</span><input type="number" value={form.importe} onChange={(e) => setForm({ ...form, importe: Number(e.target.value || 0) })} data-testid="pg-importe" /></label>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button className="btn ghost sm" onClick={prorratear} disabled={!inv} data-testid="pg-prorratear">Prorratear</button>
            </div>
          </div>

          <div className="grid g2" style={{ gap: 10, marginBottom: 14 }}>
            <label className="f"><span>Forma de pago SAT</span>
              <select value={form.forma} onChange={(e) => setForm({ ...form, forma: e.target.value })} data-testid="pg-forma">
                <option value="">Elige</option>
                {(cat?.formas_pago_sat || []).filter((f) => (f.clave || f.codigo) !== "99").map((f) => <option key={f.clave || f.codigo} value={f.clave || f.codigo}>{f.clave || f.codigo} {f.nombre}</option>)}
              </select>
            </label>
            <label className="f"><span>Medio de pago (banco)</span>
              <select value={form.medio_pago} onChange={(e) => setForm({ ...form, medio_pago: e.target.value })} data-testid="pg-medio">
                <option value="">— Elige —</option>
                {(cat?.medios_pago || []).map((m) => <option key={m.codigo} value={m.codigo}>{m.nombre}</option>)}
              </select>
            </label>
            <label className="f" style={{ gridColumn: "1 / -1" }}><span>Referencia</span><input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} data-testid="pg-referencia" /></label>
          </div>

          {inv && (
            <>
              <h4 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700 }}>Distribución</h4>
              <div className="card">
                <div className="tw">
                  <table>
                    <thead><tr><th>Expediente</th><th className="r">Total parcial</th><th className="r">Saldo</th><th className="r">Aplicar</th></tr></thead>
                    <tbody>
                      {Object.entries(dist).map(([expId, r]) => (
                        <tr key={expId} data-testid={`pg-dist-${expId}`}>
                          <td><b>{r.folio}</b></td>
                          <td className="r mono">{fmt(r.total_parcial)}</td>
                          <td className="r mono">{fmt(r.saldo)}</td>
                          <td className="r"><input type="number" value={r.importe_aplicado ?? 0}
                            onChange={(e) => updateImporte(expId, e.target.value)}
                            style={{ width: 110, textAlign: "right" }} data-testid={`pg-imp-${expId}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="r"><b>Suma distribuida</b></td>
                        <td className="r mono"><b>{fmt(sumDist)}</b></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {Math.abs(sumDist - Number(form.importe || 0)) > 0.5 && (
                <p style={{ marginTop: 8, color: "#b45309", fontSize: 12 }}>La distribución debe coincidir con el importe total ({fmt(form.importe)}).</p>
              )}
            </>
          )}
        </div>
        <div className="dlg-foot">
          <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
          <button className="btn sm" onClick={submit} data-testid="pg-guardar">Aplicar pago</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
